(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;
  const body = document.body;
  let observer;

  const updateHeader = () => body.classList.toggle('is-scrolled', window.scrollY > 18);

  const reveal = (scope = document) => {
    if (prefersReducedMotion || !observer) return;

    const elements = scope.querySelectorAll(
      '.fb-category-rail__link, [data-testid="product-list"] .resource-list__item, [class*="ai-instagram-grid__item-"]'
    );

    elements.forEach((element, index) => {
      if (element.hasAttribute('data-fb-reveal')) return;
      element.setAttribute('data-fb-reveal', '');
      const isEditorialImage = element.className.includes('ai-instagram-grid__item-');
      element.style.transitionDelay = isEditorialImage ? '0ms' : `${Math.min(index % 6, 5) * 45}ms`;
      observer.observe(element);
    });
  };

  const updateManifesto = () => {
    if (prefersReducedMotion) return;

    document.querySelectorAll('.fb-manifesto').forEach((section) => {
      const rect = section.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const headerHeight = Number.parseFloat(getComputedStyle(body).getPropertyValue('--header-height')) || 0;
      const scrollRange = Math.max(1, rect.height - viewport + headerHeight);
      const progress = Math.max(0, Math.min(1, (headerHeight - rect.top) / scrollRange));
      const eased = progress * progress * (3 - 2 * progress);
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const lateralTravel = isMobile ? 10 : 42;

      section.dataset.fbScrollProgress = progress.toFixed(3);
      section.style.setProperty('--fb-manifesto-title-shift', `${44 - eased * 88}px`);
      section.style.setProperty('--fb-manifesto-title-scale', `${0.94 + eased * 0.1}`);
      section.style.setProperty('--fb-manifesto-title-opacity', '1');
      section.style.setProperty('--fb-manifesto-copy-shift', `${62 - eased * 104}px`);
      section.style.setProperty('--fb-manifesto-copy-x', `${lateralTravel - eased * lateralTravel * 2}px`);
      section.style.setProperty('--fb-manifesto-copy-opacity', '1');
      section.style.setProperty('--fb-manifesto-meta-shift', `${30 - eased * 60}px`);
      section.style.setProperty('--fb-manifesto-meta-x', `${lateralTravel * -0.55 + eased * lateralTravel * 1.1}px`);
      section.style.setProperty('--fb-manifesto-meta-opacity', '1');
    });
  };

  const bindCategoryRail = (scope = document) => {
    scope.querySelectorAll('.fb-category-rail__link').forEach((link) => {
      if (link.dataset.fbTouchStateBound) return;
      link.dataset.fbTouchStateBound = 'true';

      link.addEventListener('click', () => {
        if (!window.matchMedia('(max-width: 749px), (hover: none), (pointer: coarse)').matches) return;
        link.closest('.fb-category-rail')?.querySelectorAll('.is-selected').forEach((item) => {
          item.classList.remove('is-selected');
        });
        link.classList.add('is-selected');
      });
    });
  };

  if (!prefersReducedMotion && 'IntersectionObserver' in window) {
    root.classList.add('fb-motion-ready');
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('fb-is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px 18% 0px', threshold: 0.01 }
    );
    reveal();
  }

  bindCategoryRail();

  let ticking = false;
  const updateMotion = () => {
    updateHeader();
    if (!prefersReducedMotion) {
      const hero = document.querySelector('.content-for-layout[data-template="index"] .hero');
      if (hero) {
        const shift = Math.min(window.scrollY * 0.045, 24);
        hero.style.setProperty('--fb-hero-shift', `${shift}px`);
      }
      updateManifesto();
    }
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateMotion);
    },
    { passive: true }
  );

  document.addEventListener('shopify:section:load', (event) => {
    reveal(event.target);
    bindCategoryRail(event.target);
    updateManifesto();
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.fb-category-rail__link.is-selected').forEach((link) => {
      link.classList.remove('is-selected');
    });
    updateManifesto();
  });

  let sizeUnit = 'in';
  try { sizeUnit = sessionStorage.getItem('fb-size-unit') === 'cm' ? 'cm' : 'in'; } catch {}
  const sizeNoteText = new WeakMap();
  const applySizeUnit = (table, unit) => {
    table.querySelectorAll('tbody td').forEach((cell) => {
      const original = cell.dataset.fbInches;
      cell.textContent = unit === 'cm'
        ? original.replace(/\d+(?:\.\d+)?/g, (number) => (Number(number) * 2.54).toFixed(1))
        : original;
    });
    const header = table.querySelector('thead th');
    if (header && /^(In|Pulg\.)$/.test(header.dataset.fbOriginal || '')) {
      header.textContent = unit === 'cm' ? 'cm' : header.dataset.fbOriginal;
    }
    const caption = table.querySelector('caption');
    if (caption) caption.textContent = unit === 'cm'
      ? caption.dataset.fbOriginal.replace(/inches/g, 'centimeters').replace(/pulgadas/g, 'centímetros')
      : caption.dataset.fbOriginal;
    const controls = table.closest('.fb-size-guide__table-wrap, .fb-size-page__table-wrap')?.previousElementSibling;
    const region = table.closest('.fb-size-guide__table-wrap, .fb-size-page__table-wrap');
    if (region?.dataset.fbOriginalLabel) region.setAttribute('aria-label', unit === 'cm'
      ? region.dataset.fbOriginalLabel.replace(/inches/g, 'centimeters').replace(/pulgadas/g, 'centímetros')
      : region.dataset.fbOriginalLabel);
    controls?.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.unit === unit)));
  };
  const updateSizeNotes = () => {
    document.querySelectorAll('.fb-size-guide__note, .fb-size-page__footnote').forEach((note) => {
      const walker = document.createTreeWalker(note, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!sizeNoteText.has(node)) sizeNoteText.set(node, node.textContent);
        const original = sizeNoteText.get(node);
        const updated = sizeUnit === 'cm'
          ? original.replace(/in inches/g, 'in centimeters').replace(/en pulgadas/g, 'en centímetros').replace(/1 in\./g, '2.5 cm.').replace(/1 pulgada/g, '2,5 cm')
          : original;
        if (node.textContent !== updated) node.textContent = updated;
      }
    });
  };
  const bindSizeUnits = () => {
    document.querySelectorAll('.fb-size-guide__table, .fb-size-page table').forEach((table) => {
      if (table.dataset.fbSizeTable) return;
      table.dataset.fbSizeTable = 'true';
      table.querySelectorAll('tbody td').forEach((cell) => { cell.dataset.fbInches = cell.textContent.trim(); });
      const header = table.querySelector('thead th');
      if (header) header.dataset.fbOriginal = header.textContent.trim();
      const caption = table.querySelector('caption');
      if (caption) caption.dataset.fbOriginal = caption.textContent;
      const region = table.closest('.fb-size-guide__table-wrap, .fb-size-page__table-wrap');
      if (region) region.dataset.fbOriginalLabel = region.getAttribute('aria-label') || '';
      const controls = document.createElement('div');
      controls.className = 'fb-size-units';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', document.documentElement.lang.startsWith('es') ? 'Unidad de medida' : 'Measurement unit');
      ['in', 'cm'].forEach((unit) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.unit = unit;
        button.textContent = unit === 'in' && document.documentElement.lang.startsWith('es') ? 'pulg.' : unit;
        button.addEventListener('click', () => {
          sizeUnit = unit;
          try { sessionStorage.setItem('fb-size-unit', unit); } catch {}
          document.querySelectorAll('[data-fb-size-table]').forEach((chart) => applySizeUnit(chart, unit));
          updateSizeNotes();
        });
        controls.append(button);
      });
      table.closest('.fb-size-guide__table-wrap, .fb-size-page__table-wrap')?.before(controls);
      applySizeUnit(table, sizeUnit);
    });
    updateSizeNotes();
  };

  const placeRestockButton = () => {
    const state = document.querySelector('[data-fb-product-state]')?.dataset.fbProductState;
    const container = document.querySelector('.restock-rocket-button-container-float-right');
    if (!state || !container) return;

    if (state === 'archived') {
      container.hidden = true;
      return;
    }

    if (state === 'sold-out' && !container.dataset.fbInline) {
      const buyButtons = document.querySelector('.buy-buttons-block');
      if (buyButtons) {
        buyButtons.insertAdjacentElement('afterend', container);
        container.dataset.fbInline = 'true';
      }
    }
  };

  if ('MutationObserver' in window) {
    const restockObserver = new MutationObserver(() => { placeRestockButton(); bindSizeUnits(); });
    restockObserver.observe(document.body, { childList: true, subtree: true });
  }
  placeRestockButton();
  bindSizeUnits();
  updateMotion();
})();
